import React, { useContext, useEffect, useState, useCallback, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Password } from "primereact/password";
import { useNavigate, useSearchParams } from "react-router-dom";
import { isAxiosError } from "axios";
import { Context } from "../../../index";
import { AppContext } from "../../../App";
import CustomLink from "../../Shared/CustomLink/CustomLink";
import PrivacyPolicyLink from "../../Shared/PrivacyPolicyLink";
import Logo from "../../../assets/mainLogo.svg";
import Background from "../../../assets/backgroundLogin.jpg";
import "../../../globalStyles/authorization.pc.css";

const MIN_PASSWORD_LENGTH = 8;

export interface YaAuthSuggest {
    init: (
        options: YaAuthOptions,
        redirectUri: string,
        buttonOptions: YaButtonOptions
    ) => Promise<YaAuthHandler>;
}

export interface YaAuthOptions {
    client_id: string;
    response_type: string;
    redirect_uri: string;
}

export interface YaButtonOptions {
    view: string;
    parentId: string;
    buttonSize: string;
    buttonView: string;
    buttonTheme: string;
    buttonBorderRadius: string;
    buttonIcon: string;
}

export interface YaAuthResult {
    access_token: string;
    expires_in: number;
    token_type: string;
}

export interface YaAuthHandler {
    handler: () => Promise<YaAuthResult>;
}

declare global {
    interface Window {
        YaAuthSuggest: YaAuthSuggest;
    }
}

interface FormData {
    email: string;
    password: string;
    passwordRepeat: string;
    firstName: string;
    surname: string;
    middleName: string;
}

interface FormErrors {
    email: string;
    firstName: string;
    surname: string;
    password: string;
    passwordRepeat: string;
}

const RegistrationForm = observer(() => {
    const [infoMode, setInfoMode] = useState(false);
    const [formData, setFormData] = useState<FormData>({
        email: "",
        password: "",
        passwordRepeat: "",
        firstName: "",
        surname: "",
        middleName: ""
    });
    const [errors, setErrors] = useState<FormErrors>({
        email: "",
        firstName: "",
        surname: "",
        password: "",
        passwordRepeat: ""
    });
    const [companyData, setCompanyData] = useState({
        name: "Development CS",
        description: "Слоган компании пользователя",
        logo: Logo,
        cover: Background
    });

    const { showToast } = useContext(AppContext);
    const { store } = useContext(Context);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const returnParam = searchParams.get("returnUrl");
    if (returnParam) {
        store.setReturnUrl(returnParam);
    }

    const isValidEmail = useCallback((email: string): boolean => {
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return emailRegex.test(email);
    }, []);

    const validateField = useCallback((field: keyof FormErrors, value: string, compareValue?: string): string => {
        if (!value.trim()) {
            switch (field) {
                case 'email': return "Введите email";
                case 'firstName': return "Заполните имя";
                case 'surname': return "Заполните фамилию";
                case 'password': return "Введите пароль";
                case 'passwordRepeat': return "Введите подтверждение пароля";
                default: return "";
            }
        }

        switch (field) {
            case 'email':
                return !isValidEmail(value) ? "Некорректный email" : "";
            case 'password':
                return value.length < MIN_PASSWORD_LENGTH ? `Минимальная длина пароля - ${MIN_PASSWORD_LENGTH} символов` : "";
            case 'passwordRepeat':
                return value !== compareValue ? "Пароли не совпадают" : "";
            default:
                return "";
        }
    }, [isValidEmail]);

    const validateForm = useCallback((): boolean => {
        const newErrors: FormErrors = {
            email: validateField('email', formData.email),
            firstName: validateField('firstName', formData.firstName),
            surname: validateField('surname', formData.surname),
            password: validateField('password', formData.password),
            passwordRepeat: validateField('passwordRepeat', formData.passwordRepeat, formData.password)
        };

        setErrors(newErrors);
        return Object.values(newErrors).every(error => !error);
    }, [formData, validateField]);

    const handleRegister = useCallback(async () => {
        if (!validateForm()) return;

        try {
            const res = await store.registration(
                formData.email,
                formData.password,
                formData.firstName.trim(),
                formData.surname.trim(),
                formData.middleName.trim()
            );

            if (res === 409) {
                setErrors(prev => ({ ...prev, email: "Пользователь с таким email уже существует" }));
                return;
            }
            setInfoMode(true);
        } catch (err) {
            showToast("Произошла ошибка при регистрации");
        }
    }, [formData, store, validateForm, showToast]);

    const handleInputChange = useCallback((field: keyof FormData) =>
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            setFormData(prev => ({ ...prev, [field]: value }));

            if (errors[field as keyof FormErrors]) {
                setErrors(prev => ({ ...prev, [field]: "" }));
            }
        },
        [errors]);

    const handleBlur = useCallback((field: keyof FormErrors, compareField?: keyof FormData) => () => {
        const compareValue = compareField ? formData[compareField] : undefined;
        const error = validateField(field, formData[field as keyof FormData], compareValue);
        setErrors(prev => ({ ...prev, [field]: error }));
    }, [formData, validateField]);

    const loadCompanyData = useCallback(async () => {
        try {
            const [settings, logo, cover] = await Promise.all([
                store.getPublicSettings(),
                store.commonService.getSystemLogo(),
                store.commonService.getSystemCover()
            ]);

            const newCompanyData = {
                name: settings?.find(x => x.paramName === "name")?.value || "Development CS",
                description: settings?.find(x => x.paramName === "description")?.value || "Слоган компании пользователя",
                logo: logo.data?.length > 0 ? logo.data : Logo,
                cover: cover.data?.length > 0 ? cover.data : Background
            };

            setCompanyData(newCompanyData);
        } catch (error) {
            console.error("Failed to load company data:", error);
        }
    }, [store]);

    const initializeUserData = useCallback(() => {
        if (store.linkNewPerson && store.userWithStatus.email) {
            setFormData(prev => ({
                ...prev,
                firstName: store.userWithStatus.name || "",
                surname: store.userWithStatus.surname || "",
                middleName: store.userWithStatus.middlename || "",
                email: store.userWithStatus.email || ""
            }));
        }
    }, [store]);

    const reloadPage = useCallback(() => {
        navigate("/login");
    }, [navigate]);

    const handleOauth = useCallback(async (access_token: string) => {
        if (!access_token) return;

        try {
            await store.loginOAuth(access_token);
            const returnUrl = store.getReturnUrl();
            navigate(returnUrl || "/");
        } catch (err) {
            if (isAxiosError(err)) {
                showToast(err.response?.data.message);
            }
        }
    }, [store, navigate, showToast]);

    const initializeYandexOAuth = useCallback(() => {
        if (!window.YaAuthSuggest) return;

        window.YaAuthSuggest.init(
            {
                client_id: store.getOAuthId(),
                response_type: "token",
                redirect_uri: `${store.getExtUrl()}/login/token`,
            },
            store.getExtUrl(),
            {
                view: "button",
                parentId: "oauth_container",
                buttonSize: "l",
                buttonView: "additional",
                buttonTheme: "light",
                buttonBorderRadius: "6",
                buttonIcon: "ya",
            }
        )
            .then((result) => result.handler())
            .then((authResult) => handleOauth(authResult.access_token))
            .catch((error: Error) => console.log("OAuth error:", error));
    }, [store, handleOauth]);

    const loadYandexScript = useCallback(() => {
        const script = document.createElement("script");
        script.src = "https://yastatic.net/s3/passport-sdk/autofill/v1/sdk-suggest-with-polyfills-latest.js";
        script.async = true;
        script.onload = initializeYandexOAuth;

        const oAuthContainer = document.querySelector(".login_in_with_Google.pointer_custom");
        oAuthContainer?.appendChild(script);
    }, [initializeYandexOAuth]);

    useEffect(() => {
        const initialize = async () => {
            await loadCompanyData();
            initializeUserData();
            loadYandexScript();
        };

        initialize();
    }, [loadCompanyData, initializeUserData, loadYandexScript]);

    const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
        if (e.key === "Enter") {
            handleRegister();
        }
    }, [handleRegister]);

    const nameFields = useMemo(() => [
        {
            id: "login_surname",
            field: "surname" as const,
            label: "Фамилия*",
            placeholder: "Фамилия",
            error: errors.surname
        },
        {
            id: "login_first_name",
            field: "firstName" as const,
            label: "Имя*",
            placeholder: "Имя",
            error: errors.firstName
        },
        {
            id: "middle_name",
            field: "middleName" as const,
            label: "Отчество",
            placeholder: "Отчество",
            error: ""
        }
    ], [errors]);

    const passwordFields = useMemo(() => [
        {
            id: "login_pass",
            field: "password" as const,
            label: "Пароль*",
            placeholder: "********",
            error: errors.password
        },
        {
            id: "login_pass_repeat",
            field: "passwordRepeat" as const,
            label: "Подтверждение пароля*",
            placeholder: "********",
            error: errors.passwordRepeat
        }
    ], [errors]);

    const infoContent = useMemo(() => (
        <div className="form_main_center">
            <div className="form_container form_container_fullscreen form_container_center">
                <div className="form_logo">
                    <img src={Logo} alt="Логотип компании" />
                </div>
                <div>
                    <h2 className="form_header form_text">
                        {store.linkNewPerson ? "Регистрация пройдена успешно!" : "Дождитесь активации администратором"}
                    </h2>
                </div>
                <div className="form_description">
                    <p className="form_text">
                        {store.linkNewPerson
                            ? "Перейдите на страницу входа"
                            : "Для подтверждения регистрации дождитесь активации администратором"}
                    </p>
                </div>
                <div className="form_flex_row">
                    <button className="form_flex_row_but" onClick={reloadPage}>
                        Вход
                    </button>
                </div>
            </div>
            <img src={Background} alt="Фон компании" className="background_image_fullscreen" />
        </div>
    ), [store.linkNewPerson, reloadPage]);

    if (infoMode) {
        return infoContent;
    }

    return (
        <div className="form_main" onKeyDown={handleKeyPress}>
            <div className="form_container">
                <div className="form_inner">
                    <div className="form_logo">
                        <img src={companyData.logo} alt="Логотип компании" />
                    </div>
                    <div>
                        <h2>{store.linkNewPerson ? "Завершите регистрацию" : "Регистрация"}</h2>
                    </div>

                    <div id="oauth_container" className="login_in_with_Google pointer_custom"></div>

                    <div className="login_in_with_Email form_flex_row">
                        <div></div>
                        <a href="#email">или через Email</a>
                        <div></div>
                    </div>

                    <div className={`input_form_login ${errors.email ? "input_with_error" : ""}`}>
                        <label className="input_label" htmlFor="login_email">
                            Email*
                        </label>
                        <InputText
                            id="login_email"
                            value={formData.email}
                            placeholder="email"
                            onBlur={handleBlur('email')}
                            autoComplete="email"
                            onChange={handleInputChange('email')}
                            className={errors.email ? "p-invalid" : ""}
                        />
                        {errors.email && <p className="error_message">{errors.email}</p>}
                    </div>

                    <form autoComplete="off">
                        <div className="input_form_name">
                            {nameFields.map(({ id, field, label, placeholder, error }) => (
                                <div key={id} className={`input_form_login ${error ? "input_with_error" : ""}`}>
                                    <label className="input_label" htmlFor={id}>
                                        {label}
                                    </label>
                                    <InputText
                                        id={id}
                                        value={formData[field]}
                                        placeholder={placeholder}
                                        onBlur={field !== 'middleName' ? handleBlur(field as keyof FormErrors) : undefined}
                                        onChange={handleInputChange(field)}
                                        autoComplete={field === 'middleName' ? "off" : "on"}
                                        className={error ? "p-invalid" : ""}
                                    />
                                    {error && <p className="error_message">{error}</p>}
                                </div>
                            ))}
                        </div>
                    </form>

                    {passwordFields.map(({ id, field, label, placeholder, error }) => (
                        <div key={id} className={`input_form_login ${error ? "input_with_error" : ""}`}>
                            <label className="input_label" htmlFor={id}>
                                {label}
                            </label>
                            <Password
                                id={id}
                                value={formData[field]}
                                onBlur={handleBlur(field as keyof FormErrors, 'password')}
                                onChange={handleInputChange(field)}
                                feedback={false}
                                placeholder={placeholder}
                                toggleMask
                                className={error ? "p-invalid" : ""}
                            />
                            {error && <p className="error_message">{error}</p>}
                        </div>
                    ))}

                    <div className="login_btn">
                        <Button
                            label="Зарегистрироваться"
                            onClick={handleRegister}
                            disabled={!formData.email || !formData.firstName || !formData.surname || !formData.password || !formData.passwordRepeat}
                        />
                    </div>

                    <div className="form_flex_row form__margin-top">
                        <p className="p_gray p_14_login">Уже есть аккаунт?</p>
                        <p className="p_blue p_14 pointer_custom">
                            <CustomLink to="/login">Войти</CustomLink>
                        </p>
                    </div>

                    <PrivacyPolicyLink />
                </div>
            </div>

            <div className="form_background">
                <div>
                    <h1 className="p_white h1_60">{companyData.name}</h1>
                    <h3 className="p_white h3_36">{companyData.description}</h3>
                </div>
                <img src={companyData.cover} alt="Фон компании" />
            </div>
        </div>
    );
});

export default RegistrationForm;